// Credentials in a connection URI (TTTL-84). Each engine driver has its own
// idea of what a username or password may hold, and each reports a
// violation only when the service is created — as a failed connection,
// wrapped in driver noise. Studio 3T refuses such a URI on import and asks
// for a URL-encoded one; the form does the same when Add is clicked, with
// each driver's own rules so the two never disagree.

type Part = "username" | "password";

/** Username and password as the user meant them, which is not always
 *  how a URI parser reads them: credentials end at the LAST '@' of the
 *  authority (what sits before the first '/', '?' or '#'), and the
 *  username ends at the FIRST ':'. A '/', '?' or '#' inside the
 *  credentials ends the authority early, so when the authority shows
 *  signs of that, the credentials are read back whole from a wider
 *  span: up to the last '@' before the options when a '/' cut in, or up
 *  to the last '@' before the path when a '?' or '#' did. A literal '@'
 *  in a database name, with no such sign, is left alone, as are the
 *  options. */
function credentials(uri: string): [string, string] | null {
  const schemeEnd = uri.indexOf("://");
  const rest = schemeEnd === -1 ? uri : uri.slice(schemeEnd + 3);
  const authority = rest.split(/[/?#]/, 1)[0];
  const beforePath = rest.split("/", 1)[0];
  const beforeOptions = rest.split(/[?#]/, 1)[0];
  let span: string;
  if (slashCutIn(authority, beforeOptions)) {
    span = beforeOptions;
  } else if (authority.includes("@")) {
    span = authority;
  } else if (beforePath.includes("@")) {
    span = beforePath;
  } else {
    return null;
  }
  const userinfo = span.slice(0, span.lastIndexOf("@"));
  const colon = userinfo.indexOf(":");
  return colon === -1
    ? [userinfo, ""]
    : [userinfo.slice(0, colon), userinfo.slice(colon + 1)];
}

/** Whether a '/' inside the credentials ended the authority early: an
 *  '@' turns up later, before the options, and either what follows it
 *  reads as the real host:port, or what the authority took for a port
 *  is no number. A plain '@' in a database name shows neither. */
function slashCutIn(authority: string, beforeOptions: string): boolean {
  const at = beforeOptions.lastIndexOf("@");
  if (at === -1 || at < authority.length) return false;
  const afterAt = beforeOptions.slice(at + 1);
  const port = authority.slice(authority.lastIndexOf("@") + 1).split(":")[1];
  return (
    /^[^/:@]+:\d+(?:\/|$)/.test(afterAt) ||
    (port !== undefined && !/^\d*$/.test(port))
  );
}

function hasAny(part: string, chars: ReadonlySet<string>): boolean {
  return [...part].some((c) => chars.has(c));
}

// A '%' must start a two-hex-digit escape.
function hasBareEscape(part: string): boolean {
  return part
    .split("%")
    .slice(1)
    .some((tail) => !/^[0-9a-fA-F]{2}/.test(tail));
}

function firstProblem(
  uri: string,
  describe: (part: Part, value: string) => string | null,
): string | null {
  const parts = credentials(uri.trim());
  if (!parts) return null;
  const [username, password] = parts;
  return describe("username", username) ?? describe("password", password);
}

// --- MongoDB -----------------------------------------------------------

// The driver's USERINFO_RESERVED_CHARACTERS.
const MONGO_RESERVED = new Set([":", "/", "?", "#", "[", "]", "@"]);

const MONGO_HINT =
  "URL-encode the username and password (for example '@' as %40 and ':' as %3A) and paste the encoded URI.";

/** Why the MongoDB driver would refuse this URI's credentials, or null
 *  when it would accept them. Text is for the error toast. */
export function mongoCredentialError(uri: string): string | null {
  return firstProblem(uri, (part, value) => {
    if (hasAny(value, MONGO_RESERVED)) {
      return `The ${part} contains characters that must be URL-encoded, such as ':' or '@'. ${MONGO_HINT}`;
    }
    if (hasBareEscape(value)) {
      return `The ${part} contains an unescaped '%'. ${MONGO_HINT}`;
    }
    return null;
  });
}

// --- PostgreSQL --------------------------------------------------------

// The engine's driver (sqlx) parses with the WHATWG `url` crate and
// percent-decodes the credentials: '@' and ':' it re-encodes itself, and
// a bare '%' passes through. These three end the address instead.
const POSTGRES_BREAKING = new Set(["/", "?", "#"]);

const POSTGRES_HINT =
  "URL-encode the username and password (for example '/' as %2F, '?' as %3F and '#' as %23) and paste the encoded URI.";

/** Why the PostgreSQL driver would misread this URI's credentials, or
 *  null when it would take them. Text is for the error toast. */
export function postgresCredentialError(uri: string): string | null {
  return firstProblem(uri, (part, value) =>
    hasAny(value, POSTGRES_BREAKING)
      ? `The ${part} contains '/', '?' or '#', which end the address. ${POSTGRES_HINT}`
      : null,
  );
}
